<template>
    <div id="await-reply" class="container-fluid">
        <a-table
            :key="'vtable-' + renderCount"
            :columns="columns()"
            :dataSource="getWaitingOfflineSupportRequests"
            :locale="{
                emptyText: $t('message.noVisitorFound'),
            }"
            :pagination="false"
            class="custom-table2"
            rowKey="_id"
            size="small"
            @change="tableChange"
        >
            <div slot="_createdDate" slot-scope="fullname, item">
                {{ item.createdDate | date }}
            </div>
            <div slot="_messageSlot" slot-scope="fullname, item">
                {{
                    (item.message || "").length > 100
                        ? _.take(item.message || "", 100)
                              .concat("...")
                              .join("")
                        : item.message || ""
                }}
            </div>
            <div slot="_actions" slot-scope="fullname, item">
                <div class="d-flex w-100 justify-content-start">
                    <a-button class="actionButton d-flex align-items-center" @click="view(item._id, item.index)">
                        <span style="margin-right: 16px; display: block">{{ $t("message.sendResponse") }}</span>
                        <ActionEdit />
                    </a-button>
                    <a-button
                        class="actionButton d-flex align-items-center"
                        @click="deleteRequest(item._id, item.index)"
                    >
                        <span style="margin-right: 16px; display: block">{{ $t("message.delete") }}</span>
                        <ActionDelete />
                    </a-button>
                </div>
            </div>
        </a-table>
        <div class="d-flex justify-content-end align-items-center mt-3">
            <a-button :loading="loading" class="mr-3" size="small" type="primary" @click="fetchData">
                <div v-if="loading">{{ $t("message.loading") }}</div>
                <div v-else>
                    <a-icon type="sync" />
                    {{ $t("message.refresh") }}
                </div>
            </a-button>
            <Pagination :page-change="pageChange" :page-sizes="pageSizes" :total="waitings.total" />
        </div>
        <b-modal id="responseForm" :title="$t('message.responseForm')" centered hide-footer size="lg" @hidden="reset">
            <b-form v-if="viewingItem" class="d-flex flex-column justify-content-end" @submit.prevent="submit">
                <b-row>
                    <b-col md="12">
                        <a-descriptions :title="$t('message.clientRequestInfo')" bordered>
                            <a-descriptions-item :label="$t('message.status')" :span="3">
                                <a-badge :text="$t('message.' + viewingItem.status)" status="processing" />
                            </a-descriptions-item>

                            <a-descriptions-item :label="$t('message.fullName')" :span="2">
                                {{ viewingItem.fullname }}
                            </a-descriptions-item>
                            <a-descriptions-item :label="$t('message.createdDate')" :span="2">
                                {{ moment(viewingItem.createdDate).format("DD/MM/YY HH:mm") }}
                            </a-descriptions-item>
                            <a-descriptions-item :label="$t('message.email')" :span="3">
                                {{ viewingItem.email }}
                            </a-descriptions-item>
                            <a-descriptions-item :label="$t('message.phone')" :span="3">
                                {{ viewingItem.phone }}
                            </a-descriptions-item>
                            <a-descriptions-item :label="$t('message.descMessage')" :span="3">
                                {{ viewingItem.message }}
                            </a-descriptions-item>
                        </a-descriptions>
                    </b-col>

                    <b-col md="12 mt-2">
                        <ckeditor
                            id="response-editor"
                            :key="'ckeditor-' + $i18n.locale"
                            ref="`ckeditor`"
                            v-model="response"
                            :config="editorConfig()"
                            :editor="editor"
                            class="border-0 w-100"
                            style="height: 300px"
                            tag-name="textarea"
                        />
                    </b-col>
                </b-row>

                <b-row class="mt-2">
                    <b-col md="12">
                        <b-button :disabled="responseSending" class="float-right" type="submit" variant="primary">
                            <template v-if="!responseSending">
                                {{ $t("message.send") }}
                            </template>

                            <template v-else>
                                <font-awesome-icon icon="spinner" spin />
                                {{ $t("message.sending") }}...
                            </template>
                        </b-button>
                        <b-button class="float-right mr-2" @click="() => this.$bvModal.hide('responseForm')">
                            <template>
                                {{ $t("message.cancel") }}
                            </template>
                        </b-button>
                    </b-col>
                </b-row>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
import moment from "moment";
import { Settings } from "luxon";
import { mapActions, mapGetters, mapState } from "vuex";
import * as _ from "lodash";
import { log2 } from "../utils/log2";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { tableSorter } from "../utils/utils";
import Pagination from "./Pagination";
import ActionEdit from "./svg/ActionEdit";
import ActionDelete from "./svg/ActionDelete";

Settings.defaultLocale = "tr";

const log = log2("AwaitReply");
export default {
    name: "AwaitReply",
    components: {
        Pagination,
        ActionDelete,
        ActionEdit,
    },
    data() {
        return {
            response: "",
            responseSending: false,
            viewingItem: undefined,
            columns: () => [
                {
                    title: this.$t("message.fullName"),
                    dataIndex: "fullname",
                    width: "20%",
                    ...tableSorter("fullname", "string", this.currentSort),
                },
                {
                    title: this.$t("message.email"),
                    dataIndex: "email",
                    width: "20%",
                    ...tableSorter("email", "string", this.currentSort),
                },
                {
                    title: this.$t("message.phone"),
                    dataIndex: "phone",
                    width: "10%",
                    ...tableSorter("phone", "string", this.currentSort),
                },
                {
                    title: this.$t("message.date"),
                    scopedSlots: { customRender: "_createdDate" },
                    width: "10%",
                    slot: "createdDate",
                    ...tableSorter("createdDate", "date", this.currentSort),
                },
                {
                    title: this.$t("message.message"),
                    scopedSlots: { customRender: "_messageSlot" },
                    width: "30%",
                    slot: "_messageSlot",
                },
                {
                    title: this.$t("message.transactions"),
                    scopedSlots: { customRender: "_actions" },
                    slot: "actions",
                    width: "10%",
                },
            ],
            rowIndex: 0,
            pageSizes: ["10", "25", "50", "100"],
            pageSize: 10,
            current: 1,
            currentSort: {
                columnKey: "createdDate",
                order: "ascend",
            },
            renderCount: 0,
            phrases: {
                ok: this.$t("message.ok"),
                cancel: this.$t("message.cancel"),
            },
            editor: ClassicEditor,
            editorConfig: () => ({
                // The configuration of the editor.
                toolbar: ["bold", "italic", "link"],
                placeholder: this.$t("message.typeHere"),
            }),
            loading: false,
        };
    },
    computed: {
        ...mapState({
            offlineMode: (state) => state.setting.offlineMode,
            waitings: (state) => state.offlineSupportRequest.waitings,
            users: (state) => state.user.users,
            user: (state) => state.auth.user,
        }),
        ...mapGetters({
            getUser: "user/getUser",
            getWaitingOfflineSupportRequests: "offlineSupportRequest/getWaitingOfflineSupportRequests",
        }),
        _() {
            return _;
        },
        moment() {
            return moment;
        },
    },
    methods: {
        ...mapActions({
            getOfflineSupportRequests: "offlineSupportRequest/getOfflineSupportRequests",
            deleteOfflineSupportRequest: "offlineSupportRequest/deleteOfflineSupportRequest",
            sendResponse: "offlineSupportRequest/sendResponse",
            fetchWaitingOfflineSupportRequests: "offlineSupportRequest/fetchWaitingOfflineSupportRequests",
        }),
        tableChange(pagination, filters, sorter) {
            log("tableChange", { pagination, filters, sorter });
            this.perPage = pagination.pageSize;
            log("tableChange", { pagination, filters, sorter });
            const newSort = {
                columnKey: sorter.columnKey,
                order: sorter.order,
            };
            const oldSort = this.currentSort;
            log("show me current Sort", {
                currentSort: this.currentSort,
                newSort,
            });
            if (!_.isEqual(newSort, oldSort)) {
                this.currentSort = newSort;
                this.renderCount += 1;
                this.$forceUpdate();
            }
        },

        onShowSizeChange(current, pageSize) {
            log("onShowSizeChange", current, pageSize);
            this.current = current;
            this.pageSize = pageSize;
            this.fetchWaitingOfflineSupportRequests({
                perPage: pageSize,
                page: current,
            });
        },
        fetchData() {
            this.loading = true;
            this.fetchWaitingOfflineSupportRequests({
                perPage: this.pageSize,
                page: this.current,
            }).finally((x) => {
                this.loading = false;
            });
        },
        pageChange(page, pageSize) {
            log("pageChange", page, pageSize, typeof page);
            this.current = page;
            this.pageSize = pageSize;
            this.fetchData();
        },
        deleteRequest(id, index) {
            this.$dialog
                .confirm(this.$t("message.doYouWishToOfflineSupportRequest"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    let deletingItem = this.waitings.data.find((x) => x._id === id);
                    log("deleteRequest", { id, index, deletingItem });
                    if (deletingItem) {
                        this.deleteOfflineSupportRequest(deletingItem._id)
                            .then((x) => {
                                this.$toasted.show(this.$t("message.deletedSuccessfully"), {
                                    duration: 3000,
                                    position: "bottom-center",
                                });
                            })
                            .catch((err) => {
                                this.$toasted.error(this.$t("message.deleteErrorMsg"), {
                                    duration: 3000,
                                    position: "bottom-center",
                                });
                            });
                    }
                });
        },
        submit() {
            log("submit called");
            if (this.response && this.response.length > 0) {
                this.responseSending = true;
                this.sendResponse({
                    userId: this.user._id,
                    response: this.response,
                    offlineSupportRequestId: this.viewingItem._id,
                })
                    .then((x) => {
                        this.$bvModal.hide("responseForm");
                        this.$toasted.show(this.$t("message.messageSent"), {
                            duration: 3000,
                            position: "bottom-center",
                        });
                        this.fetchData();
                        this.reset();
                    })
                    .catch((err) => {
                        log.error("cevap gönderilemedi", err);
                        this.$toasted.error(this.$t("message.messageSentErr"), {
                            duration: 3000,
                            position: "bottom-center",
                        });
                    })
                    .finally(() => {
                        this.responseSending = false;
                    });
            }
        },

        view(id) {
            let item = this.waitings.data.find((x) => x._id === id);
            this.viewingItem = item;
            this.$bvModal.show("responseForm");
        },
        reset() {
            this.response = "";
            this.viewingItem = undefined;
        },
    },
    mounted() {
        moment.locale(this.$i18n.locale);
        this.fetchData();
        this.refreshTimerId = setInterval(this.fetchData, 10000);
    },
    updated() {
        if (moment.locale() !== this.$i18n.locale) moment.locale(this.$i18n.locale);
    },
    beforeDestroy() {
        clearInterval(this.refreshTimerId);
    },
    watch: {
        pageSize(newValue) {
            log("pageSize", newValue);
            this.pageSize = newValue;
            this.fetchData();
        },
        current(newValue) {
            log("current", newValue);
            this.current = newValue;
            this.fetchData();
        },
    },
};
</script>

<style lang="scss">
.new-theme {
    #visitor-history {
    }

    .custom-table {
        background-color: #ffffff;
        border-radius: 6px;
        padding: 10px 20px;
    }

    .ant-table-column-sorters,
    .ant-table-header-column {
        width: 100%;
    }

    .ant-table-header-column {
        padding: 0 10px;
    }

    .table-custom,
    .ant-table-placeholder,
    .ant-table-small {
        border: none;
    }

    .ant-table-thead tr {
    }

    .ant-table-body {
        tr {
        }

        td {
            font-family: Poppins;
            padding: 0 8px !important;
            font-size: 12px;
            font-weight: 300;
            color: #181c32;
            border-bottom: none;
        }

        th {
            border-right: 1px solid #ebedf3;
            border-bottom: none;
            padding: 0 !important;

            &:last-child {
                border-right: none;
            }
        }
    }

    .ant-table-body tbody td {
        border-right: 1px solid #ebedf3;

        &:last-child {
            border-right: none;
        }
    }

    .ant-table-column-title {
        color: #181c32;
        font-size: 13px;
        font-weight: 300;
        font-family: Poppins;
        white-space: nowrap;
    }

    .actionButton {
        border: none;
        box-shadow: none;
    }
}
</style>
